import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Label,
  ConfirmDialog,
  ResultDialog,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { stablecoinContractApi } from "@/services/api";
import {
  Coins,
  Plus,
  Plug,
  CheckCircle,
  XCircle,
  Loader2,
  Star,
  MoreHorizontal,
  Pencil,
  Trash2,
  Power,
  X,
  RefreshCw,
  ExternalLink,
  Activity,
  Pause,
  Play,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  ArrowRightLeft,
  Key,
  AlertTriangle,
} from "lucide-react";

interface StablecoinContract {
  id: string;
  name: string;
  address: string;
  networkId: string;
  isActive: boolean;
  cachedName?: string;
  cachedSymbol?: string;
  cachedDecimals?: number;
  cachedTotalSupply?: string;
  cachedOwner?: string;
  cachedAt?: string;
  lastTestedAt?: string;
  lastTestStatus?: string;
  lastTestError?: string;
  createdAt: string;
  updatedAt: string;
}

interface ContractInfo {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  totalSupplyFormatted: string;
  owner: string;
  address: string;
  networkId: string;
  networkName: string;
  explorerUrl?: string;
}

interface ContractEvent {
  eventName: string;
  blockNumber: number;
  transactionHash: string;
  from?: string;
  to?: string;
  amount?: string;
  amountFormatted?: string;
  account?: string;
  role?: string;
  roleName?: string;
  sender?: string;
  reference?: string;
}

interface ContractStatus {
  isPaused: boolean;
  totalSupply: string;
  totalSupplyFormatted: string;
}

interface FormData {
  id: string;
  name: string;
  address: string;
  networkId: string;
  abi: string;
  isActive: boolean;
}

const initialFormData: FormData = {
  id: "",
  name: "",
  address: "",
  networkId: "",
  abi: "",
  isActive: false,
};

// ABI padrão do SimpleBRLD/BRLD
const defaultABI = JSON.stringify(
  [
    "function name() view returns (string)",
    "function symbol() view returns (string)",
    "function decimals() view returns (uint8)",
    "function totalSupply() view returns (uint256)",
    "function balanceOf(address) view returns (uint256)",
    "function owner() view returns (address)",
    "function mint(address to, uint256 amount)",
    "function burn(address from, uint256 amount)",
    "function transfer(address to, uint256 amount) returns (bool)",
    "event Transfer(address indexed from, address indexed to, uint256 value)",
    "event Minted(address indexed to, uint256 amount)",
    "event Burned(address indexed from, uint256 amount)",
  ],
  null,
  2
);

export function StablecoinContractPage() {
  const [contracts, setContracts] = useState<StablecoinContract[]>([]);
  const [contractInfoMap, setContractInfoMap] = useState<Record<string, ContractInfo | null>>({});
  const [eventsMap, setEventsMap] = useState<Record<string, ContractEvent[]>>({});
  const [selectedContractForEvents, setSelectedContractForEvents] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [loadingInfo, setLoadingInfo] = useState<Record<string, boolean>>({});
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [contractStatusMap, setContractStatusMap] = useState<Record<string, ContractStatus | null>>({});
  const [loadingStatus, setLoadingStatus] = useState<Record<string, boolean>>({});

  // Estados para ações administrativas
  const [adminAction, setAdminAction] = useState<{
    type: "pause" | "unpause" | "blacklist-add" | "blacklist-remove" | "clawback" | "grant-role" | "revoke-role" | null;
    contractId: string;
    loading: boolean;
  }>({ type: null, contractId: "", loading: false });
  const [blacklistAddress, setBlacklistAddress] = useState("");
  const [checkBlacklistAddress, setCheckBlacklistAddress] = useState("");
  const [checkBlacklistResult, setCheckBlacklistResult] = useState<{ address: string; isBlacklisted: boolean } | null>(null);
  const [clawbackData, setClawbackData] = useState({ from: "", to: "", amount: "" });
  const [roleData, setRoleData] = useState({ address: "", role: "MINTER" as "MINTER" | "PAUSER" | "COMPLIANCE_OFFICER" });
  const [testResult, setTestResult] = useState<{
    id: string;
    success: boolean;
    name?: string;
    symbol?: string;
    decimals?: number;
    totalSupply?: string;
    owner?: string;
    latency?: number;
    error?: string;
  } | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState("");

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({
    open: false,
    variant: "error",
    title: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const contractsData = await stablecoinContractApi.list();
      setContracts(contractsData);

      // Carregar info de TODOS os contratos cadastrados
      for (const contract of contractsData) {
        loadContractInfoById(contract.id);
      }

      // Selecionar o primeiro contrato para mostrar eventos (preferência para o ativo)
      const activeContract = contractsData.find((c: StablecoinContract) => c.isActive);
      const firstContract = activeContract || contractsData[0];
      if (firstContract) {
        setSelectedContractForEvents(firstContract.id);
        loadEventsById(firstContract.id);
        loadContractStatus(firstContract.id);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadContractInfoById(contractId: string) {
    setLoadingInfo((prev) => ({ ...prev, [contractId]: true }));
    try {
      const info = await stablecoinContractApi.getInfoById(contractId);
      setContractInfoMap((prev) => ({ ...prev, [contractId]: info }));
    } catch (error) {
      console.error(`Erro ao carregar info do contrato ${contractId}:`, error);
      setContractInfoMap((prev) => ({ ...prev, [contractId]: null }));
    } finally {
      setLoadingInfo((prev) => ({ ...prev, [contractId]: false }));
    }
  }

  async function loadEventsById(contractId: string) {
    setLoadingEvents(true);
    try {
      const result = await stablecoinContractApi.getEventsById(contractId, 50);
      setEventsMap((prev) => ({ ...prev, [contractId]: result.events || [] }));
    } catch (error) {
      console.error(`Erro ao carregar eventos do contrato ${contractId}:`, error);
      setEventsMap((prev) => ({ ...prev, [contractId]: [] }));
    } finally {
      setLoadingEvents(false);
    }
  }

  function handleSelectContractForEvents(contractId: string) {
    setSelectedContractForEvents(contractId);
    if (!eventsMap[contractId]) {
      loadEventsById(contractId);
    }
    // Carregar status se ainda não tem
    if (!contractStatusMap[contractId]) {
      loadContractStatus(contractId);
    }
  }

  async function loadContractStatus(contractId: string) {
    setLoadingStatus((prev) => ({ ...prev, [contractId]: true }));
    try {
      const status = await stablecoinContractApi.getStatus(contractId);
      setContractStatusMap((prev) => ({ ...prev, [contractId]: status }));
    } catch (error) {
      console.error(`Erro ao carregar status do contrato ${contractId}:`, error);
      setContractStatusMap((prev) => ({ ...prev, [contractId]: null }));
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [contractId]: false }));
    }
  }

  // Ações administrativas
  async function handlePause(contractId: string) {
    setAdminAction({ type: "pause", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.pause(contractId);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Contrato pausado",
          description: result.explorerUrl
            ? `TX: ${result.transactionHash?.slice(0, 10)}... Ver no explorer.`
            : `TX: ${result.transactionHash}`,
        });
        loadContractStatus(contractId);
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro ao pausar", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  async function handleUnpause(contractId: string) {
    setAdminAction({ type: "unpause", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.unpause(contractId);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Contrato despausado",
          description: result.explorerUrl
            ? `TX: ${result.transactionHash?.slice(0, 10)}...`
            : `TX: ${result.transactionHash}`,
        });
        loadContractStatus(contractId);
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro ao despausar", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  async function handleAddToBlacklist(contractId: string) {
    if (!blacklistAddress) return;
    setAdminAction({ type: "blacklist-add", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.addToBlacklist(contractId, blacklistAddress);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Adicionado a blacklist",
          description: `${blacklistAddress.slice(0, 10)}... adicionado a blacklist.`,
        });
        setBlacklistAddress("");
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  async function handleRemoveFromBlacklist(contractId: string) {
    if (!blacklistAddress) return;
    setAdminAction({ type: "blacklist-remove", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.removeFromBlacklist(contractId, blacklistAddress);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Removido da blacklist",
          description: `${blacklistAddress.slice(0, 10)}... removido da blacklist.`,
        });
        setBlacklistAddress("");
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  async function handleCheckBlacklist(contractId: string) {
    if (!checkBlacklistAddress) return;
    try {
      const result = await stablecoinContractApi.checkBlacklist(contractId, checkBlacklistAddress);
      setCheckBlacklistResult(result);
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro ao verificar" });
    }
  }

  async function handleClawback(contractId: string) {
    if (!clawbackData.from || !clawbackData.to || !clawbackData.amount) return;
    setAdminAction({ type: "clawback", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.clawback(contractId, clawbackData.from, clawbackData.to, clawbackData.amount);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Clawback executado",
          description: `${clawbackData.amount} tokens transferidos de ${clawbackData.from.slice(0, 10)}... para ${clawbackData.to.slice(0, 10)}...`,
        });
        setClawbackData({ from: "", to: "", amount: "" });
        loadContractInfoById(contractId);
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  async function handleGrantRole(contractId: string) {
    if (!roleData.address) return;
    setAdminAction({ type: "grant-role", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.grantRole(contractId, roleData.address, roleData.role);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Role concedida",
          description: `${roleData.role} concedida para ${roleData.address.slice(0, 10)}...`,
        });
        setRoleData({ address: "", role: "MINTER" });
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  async function handleRevokeRole(contractId: string) {
    if (!roleData.address) return;
    setAdminAction({ type: "revoke-role", contractId, loading: true });
    try {
      const result = await stablecoinContractApi.revokeRole(contractId, roleData.address, roleData.role);
      if (result.success) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Role revogada",
          description: `${roleData.role} revogada de ${roleData.address.slice(0, 10)}...`,
        });
        setRoleData({ address: "", role: "MINTER" });
        loadEventsById(contractId);
      } else {
        setResultDialog({ open: true, variant: "error", title: "Erro", description: result.error });
      }
    } catch (error) {
      setResultDialog({ open: true, variant: "error", title: "Erro", description: error instanceof Error ? error.message : "Erro desconhecido" });
    } finally {
      setAdminAction({ type: null, contractId: "", loading: false });
    }
  }

  function openCreateModal() {
    setFormData({ ...initialFormData, abi: defaultABI });
    setEditingId(null);
    setFormError("");
    setShowModal(true);
  }

  function openEditModal(contract: StablecoinContract) {
    setFormData({
      id: contract.id,
      name: contract.name,
      address: contract.address,
      networkId: contract.networkId,
      abi: "", // ABI não é retornada, usuário precisa reenviar se quiser alterar
      isActive: contract.isActive,
    });
    setEditingId(contract.id);
    setFormError("");
    setShowModal(true);
  }

  async function handleSave() {
    setFormError("");

    if (!formData.id || !formData.name || !formData.address || !formData.networkId) {
      setFormError("ID, Nome, Endereço e Rede são obrigatórios");
      return;
    }

    if (!editingId && !formData.abi) {
      setFormError("ABI é obrigatório para novos contratos");
      return;
    }

    // Validar ABI como JSON
    let parsedAbi;
    if (formData.abi) {
      try {
        parsedAbi = JSON.parse(formData.abi);
      } catch {
        setFormError("ABI deve ser um JSON válido");
        return;
      }
    }

    setSaving(true);
    try {
      if (editingId) {
        const updateData: Record<string, unknown> = {
          name: formData.name,
          address: formData.address,
          networkId: formData.networkId,
        };
        if (formData.abi && parsedAbi) {
          updateData.abi = parsedAbi;
        }
        await stablecoinContractApi.update(editingId, updateData);
      } else {
        await stablecoinContractApi.create({
          ...formData,
          abi: parsedAbi,
        });
      }
      setShowModal(false);
      await loadData();
    } catch (error: any) {
      setFormError(error.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection(id: string) {
    setTesting(id);
    setTestResult(null);
    try {
      const result = await stablecoinContractApi.test(id);
      setTestResult({ id, ...result });
      if (result.success) {
        await loadData();
      }
    } catch (error) {
      setTestResult({
        id,
        success: false,
        error: error instanceof Error ? error.message : "Erro desconhecido",
      });
    } finally {
      setTesting(null);
    }
  }

  async function handleActivate(id: string) {
    try {
      await stablecoinContractApi.activate(id);
      await loadData();
    } catch (error) {
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao ativar",
        description: error instanceof Error ? error.message : "Não foi possível ativar o contrato. Tente novamente.",
      });
    }
  }

  function handleDelete(id: string) {
    setConfirmDialog({
      open: true,
      title: "Excluir Contrato",
      description:
        "Tem certeza que deseja excluir este contrato? Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
      variant: "danger",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await stablecoinContractApi.delete(id);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          await loadData();
        } catch (error) {
          console.error("Erro ao excluir:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  function formatAddress(address: string) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  function formatNumber(value: string | number) {
    return parseFloat(String(value)).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const activeContract = contracts.find((c) => c.isActive);

  // Calcular supply total somando todos os contratos
  const totalSupplyAll = contracts.reduce((sum, c) => {
    const info = contractInfoMap[c.id];
    if (info?.totalSupplyFormatted) {
      return sum + parseFloat(info.totalSupplyFormatted);
    }
    return sum;
  }, 0);

  const stats = [
    { value: contracts.length, label: "Contratos" },
    { value: activeContract ? 1 : 0, label: "Ativo" },
    {
      value: totalSupplyAll > 0 ? formatNumber(totalSupplyAll) : "-",
      label: "Supply Total",
    },
  ];

  const currentEvents = selectedContractForEvents ? eventsMap[selectedContractForEvents] || [] : [];
  const currentContractInfo = selectedContractForEvents ? contractInfoMap[selectedContractForEvents] : null;
  const currentStatus = selectedContractForEvents ? contractStatusMap[selectedContractForEvents] : null;

  // Helper para formatar evento com badge colorida
  function getEventBadgeVariant(eventName: string): "success" | "destructive" | "warning" | "secondary" | "outline" {
    switch (eventName) {
      case "Minted":
        return "success";
      case "Burned":
        return "destructive";
      case "AddedToBlacklist":
      case "TransferBlocked":
      case "Clawback":
        return "warning";
      case "RemovedFromBlacklist":
      case "Unpaused":
        return "success";
      case "Paused":
        return "destructive";
      case "RoleGranted":
      case "RoleRevoked":
        return "secondary";
      default:
        return "outline";
    }
  }

  // Helper para mostrar detalhes específicos do evento
  function getEventDetails(event: ContractEvent): string {
    switch (event.eventName) {
      case "AddedToBlacklist":
      case "RemovedFromBlacklist":
        return event.account ? formatAddress(event.account) : "-";
      case "RoleGranted":
      case "RoleRevoked":
        return `${event.roleName || "Role"} → ${event.account ? formatAddress(event.account) : "-"}`;
      case "Paused":
      case "Unpaused":
        return event.account ? `Por: ${formatAddress(event.account)}` : "-";
      default:
        return "-";
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Coins className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Contrato Stablecoin"
          description="Configuração do contrato BRLD para operações de mint/burn"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Coins className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Contrato Stablecoin"
        description="Configuração do contrato BRLD para operações de mint/burn"
        stats={stats}
        actions={
          <Button
            onClick={openCreateModal}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Contrato
          </Button>
        }
      />

      {/* Contract Info Cards - Mostra todos os contratos */}
      {contracts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {contracts.map((contract) => {
            const info = contractInfoMap[contract.id];
            const isLoading = loadingInfo[contract.id];

            return (
              <Card key={contract.id} className={contract.isActive ? "border-primary" : ""}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {info?.symbol || contract.cachedSymbol || "..."}
                      {contract.isActive && (
                        <Badge variant="success" className="text-xs">Ativo</Badge>
                      )}
                    </div>
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => loadContractInfoById(contract.id)}
                      >
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {info ? (
                    <>
                      <div className="text-2xl font-bold">
                        {formatNumber(info.totalSupplyFormatted)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {info.name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {info.networkName}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-bold text-muted-foreground">
                        {contract.cachedTotalSupply ? formatNumber(parseFloat(contract.cachedTotalSupply) / 1e18) : "-"}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {contract.name}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Contracts List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Contratos Cadastrados</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData()}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Atualizar Todos
          </Button>
        </CardHeader>
        <CardContent>
          {contracts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nenhum contrato cadastrado. Clique em "Novo Contrato" para adicionar.
            </p>
          ) : (
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">ID</th>
                    <th className="p-4 font-medium">Nome</th>
                    <th className="p-4 font-medium">Endereço</th>
                    <th className="p-4 font-medium">Rede</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((contract) => (
                    <tr
                      key={contract.id}
                      className="border-b border-border last:border-0 hover:bg-muted/50"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {contract.id}
                          </code>
                          {contract.isActive && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div>
                          <div>{contract.name}</div>
                          {contract.cachedSymbol && (
                            <div className="text-sm text-muted-foreground">
                              {contract.cachedSymbol} ({contract.cachedDecimals} decimais)
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {contractInfoMap[contract.id]?.explorerUrl ? (
                          <a
                            href={`${contractInfoMap[contract.id]?.explorerUrl}/address/${contract.address}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          >
                            <code className="text-sm">{formatAddress(contract.address)}</code>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <code className="text-sm">{formatAddress(contract.address)}</code>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge variant="secondary">{contract.networkId}</Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={contract.isActive ? "success" : "outline"}>
                          {contract.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                        {contract.lastTestStatus && (
                          <div className="mt-1">
                            <Badge
                              variant={
                                contract.lastTestStatus === "SUCCESS"
                                  ? "success"
                                  : "destructive"
                              }
                              className="text-xs"
                            >
                              {contract.lastTestStatus === "SUCCESS"
                                ? "Testado OK"
                                : "Teste Falhou"}
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestConnection(contract.id)}
                            disabled={testing === contract.id}
                            title="Testar conexão"
                          >
                            {testing === contract.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plug className="w-4 h-4" />
                            )}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[160px]">
                              <DropdownMenuItem onClick={() => openEditModal(contract)}>
                                <Pencil className="w-3 h-3 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              {!contract.isActive && (
                                <DropdownMenuItem onClick={() => handleActivate(contract.id)}>
                                  <Power className="w-3 h-3 mr-2" />
                                  Ativar
                                </DropdownMenuItem>
                              )}
                              {!contract.isActive && (
                                <DropdownMenuItem
                                  onClick={() => handleDelete(contract.id)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-3 h-3 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div
              className={`mt-4 p-4 rounded-lg ${
                testResult.success
                  ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              {testResult.success ? (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 mt-0.5" />
                  <div>
                    <div className="font-medium">Conexão bem-sucedida!</div>
                    <div className="text-sm mt-1">
                      <span className="font-medium">{testResult.name}</span> ({testResult.symbol})
                      <br />
                      Supply: {formatNumber(testResult.totalSupply || "0")} | Decimais:{" "}
                      {testResult.decimals}
                      <br />
                      Owner: {testResult.owner ? formatAddress(testResult.owner) : "N/A"}
                      <br />
                      Latência: {testResult.latency}ms
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <XCircle className="w-5 h-5" />
                  <span>Falha na conexão: {testResult.error}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contract Control & Events Panel */}
      {contracts.length > 0 && selectedContractForEvents && (
        <>
          {/* Admin Controls */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Painel de Controle
              </CardTitle>
              <div className="flex items-center gap-2">
                <select
                  className="text-sm border rounded px-2 py-1 bg-background"
                  value={selectedContractForEvents || ""}
                  onChange={(e) => handleSelectContractForEvents(e.target.value)}
                >
                  {contracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {contractInfoMap[c.id]?.symbol || c.cachedSymbol || c.id}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadContractStatus(selectedContractForEvents)}
                  disabled={loadingStatus[selectedContractForEvents]}
                >
                  {loadingStatus[selectedContractForEvents] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Status do Contrato */}
              <div className="mb-6 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Status:</span>
                      {currentStatus?.isPaused ? (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <Pause className="w-3 h-3" />
                          Pausado
                        </Badge>
                      ) : (
                        <Badge variant="success" className="flex items-center gap-1">
                          <Play className="w-3 h-3" />
                          Ativo
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Supply: {currentStatus?.totalSupplyFormatted ? formatNumber(currentStatus.totalSupplyFormatted) : "-"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentStatus?.isPaused ? (
                      <Button
                        size="sm"
                        onClick={() => handleUnpause(selectedContractForEvents)}
                        disabled={adminAction.loading}
                      >
                        {adminAction.type === "unpause" && adminAction.loading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Despausar
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          setConfirmDialog({
                            open: true,
                            title: "Pausar Contrato",
                            description: "Tem certeza que deseja pausar o contrato? Todas as transferências serão bloqueadas.",
                            confirmText: "Pausar",
                            variant: "danger",
                            onConfirm: () => {
                              setConfirmDialog((prev) => ({ ...prev, open: false }));
                              handlePause(selectedContractForEvents);
                            },
                            loading: false,
                          });
                        }}
                        disabled={adminAction.loading}
                      >
                        {adminAction.type === "pause" && adminAction.loading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Pause className="w-4 h-4 mr-2" />
                        )}
                        Pausar
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <Tabs defaultValue="blacklist">
                <TabsList>
                  <TabsTrigger value="blacklist" className="flex items-center gap-1">
                    <UserX className="w-4 h-4" />
                    Blacklist
                  </TabsTrigger>
                  <TabsTrigger value="clawback" className="flex items-center gap-1">
                    <ArrowRightLeft className="w-4 h-4" />
                    Clawback
                  </TabsTrigger>
                  <TabsTrigger value="roles" className="flex items-center gap-1">
                    <Key className="w-4 h-4" />
                    Roles
                  </TabsTrigger>
                </TabsList>

                {/* Blacklist Tab */}
                <TabsContent value="blacklist" className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Verificar Blacklist
                      </h4>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Endereço (0x...)"
                          value={checkBlacklistAddress}
                          onChange={(e) => setCheckBlacklistAddress(e.target.value)}
                        />
                        <Button
                          variant="outline"
                          onClick={() => handleCheckBlacklist(selectedContractForEvents)}
                        >
                          Verificar
                        </Button>
                      </div>
                      {checkBlacklistResult && (
                        <div className={`p-2 rounded text-sm ${checkBlacklistResult.isBlacklisted ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"}`}>
                          {checkBlacklistResult.isBlacklisted ? (
                            <span className="flex items-center gap-1"><ShieldOff className="w-4 h-4" /> Endereço está na blacklist</span>
                          ) : (
                            <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> Endereço NÃO está na blacklist</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="p-4 border rounded-lg space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <UserX className="w-4 h-4" />
                        Gerenciar Blacklist
                      </h4>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Endereço (0x...)"
                          value={blacklistAddress}
                          onChange={(e) => setBlacklistAddress(e.target.value)}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleAddToBlacklist(selectedContractForEvents)}
                          disabled={!blacklistAddress || adminAction.loading}
                        >
                          {adminAction.type === "blacklist-add" && adminAction.loading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <UserX className="w-4 h-4 mr-1" />
                          )}
                          Adicionar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemoveFromBlacklist(selectedContractForEvents)}
                          disabled={!blacklistAddress || adminAction.loading}
                        >
                          {adminAction.type === "blacklist-remove" && adminAction.loading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <UserCheck className="w-4 h-4 mr-1" />
                          )}
                          Remover
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Clawback Tab */}
                <TabsContent value="clawback" className="mt-4">
                  <div className="p-4 border rounded-lg space-y-4 max-w-xl">
                    <div className="flex items-start gap-2 p-3 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong>Atenção:</strong> O clawback transfere tokens de um endereço para outro de forma forçada, sem necessidade de aprovação.
                        Use apenas em casos de fraude ou compliance.
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>De (origem)</Label>
                        <Input
                          placeholder="Endereço de origem (0x...)"
                          value={clawbackData.from}
                          onChange={(e) => setClawbackData({ ...clawbackData, from: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Para (destino)</Label>
                        <Input
                          placeholder="Endereço de destino (0x...)"
                          value={clawbackData.to}
                          onChange={(e) => setClawbackData({ ...clawbackData, to: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Quantidade</Label>
                        <Input
                          placeholder="Ex: 100.00"
                          value={clawbackData.amount}
                          onChange={(e) => setClawbackData({ ...clawbackData, amount: e.target.value })}
                        />
                      </div>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setConfirmDialog({
                            open: true,
                            title: "Confirmar Clawback",
                            description: `Tem certeza que deseja transferir ${clawbackData.amount} tokens de ${clawbackData.from.slice(0, 10)}... para ${clawbackData.to.slice(0, 10)}...? Esta ação é irreversível.`,
                            confirmText: "Executar Clawback",
                            variant: "danger",
                            onConfirm: () => {
                              setConfirmDialog((prev) => ({ ...prev, open: false }));
                              handleClawback(selectedContractForEvents);
                            },
                            loading: false,
                          });
                        }}
                        disabled={!clawbackData.from || !clawbackData.to || !clawbackData.amount || adminAction.loading}
                      >
                        {adminAction.type === "clawback" && adminAction.loading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <ArrowRightLeft className="w-4 h-4 mr-2" />
                        )}
                        Executar Clawback
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Roles Tab */}
                <TabsContent value="roles" className="mt-4">
                  <div className="p-4 border rounded-lg space-y-4 max-w-xl">
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Endereço</Label>
                        <Input
                          placeholder="Endereço (0x...)"
                          value={roleData.address}
                          onChange={(e) => setRoleData({ ...roleData, address: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <select
                          className="w-full border rounded px-3 py-2 bg-background"
                          value={roleData.role}
                          onChange={(e) => setRoleData({ ...roleData, role: e.target.value as "MINTER" | "PAUSER" | "COMPLIANCE_OFFICER" })}
                        >
                          <option value="MINTER">MINTER_ROLE - Pode fazer mint de tokens</option>
                          <option value="PAUSER">PAUSER_ROLE - Pode pausar/despausar o contrato</option>
                          <option value="COMPLIANCE_OFFICER">COMPLIANCE_OFFICER_ROLE - Pode gerenciar blacklist e clawback</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleGrantRole(selectedContractForEvents)}
                          disabled={!roleData.address || adminAction.loading}
                        >
                          {adminAction.type === "grant-role" && adminAction.loading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Key className="w-4 h-4 mr-2" />
                          )}
                          Conceder Role
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleRevokeRole(selectedContractForEvents)}
                          disabled={!roleData.address || adminAction.loading}
                        >
                          {adminAction.type === "revoke-role" && adminAction.loading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <ShieldOff className="w-4 h-4 mr-2" />
                          )}
                          Revogar Role
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Events Tab */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Eventos Recentes
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadEventsById(selectedContractForEvents)}
                disabled={loadingEvents}
              >
                {loadingEvents ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </CardHeader>
            <CardContent>
              {loadingEvents ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : currentEvents.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Nenhum evento encontrado nos últimos blocos.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-3 font-medium">Evento</th>
                        <th className="p-3 font-medium">Bloco</th>
                        <th className="p-3 font-medium">De/Conta</th>
                        <th className="p-3 font-medium">Para/Detalhes</th>
                        <th className="p-3 font-medium">Valor</th>
                        <th className="p-3 font-medium">Tx</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentEvents.map((event, index) => (
                        <tr
                          key={`${event.transactionHash}-${index}`}
                          className="border-b border-border last:border-0 hover:bg-muted/50"
                        >
                          <td className="p-3">
                            <Badge variant={getEventBadgeVariant(event.eventName)}>
                              {event.eventName}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <code className="text-sm">
                              {event.blockNumber.toLocaleString()}
                            </code>
                          </td>
                          <td className="p-3">
                            {event.from ? (
                              <code className="text-sm">{formatAddress(event.from)}</code>
                            ) : event.account ? (
                              <code className="text-sm">{formatAddress(event.account)}</code>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="p-3">
                            {event.to ? (
                              <code className="text-sm">{formatAddress(event.to)}</code>
                            ) : event.roleName ? (
                              <span className="text-sm">{event.roleName}</span>
                            ) : (
                              getEventDetails(event) !== "-" ? (
                                <span className="text-sm">{getEventDetails(event)}</span>
                              ) : "-"
                            )}
                          </td>
                          <td className="p-3">
                            {event.amountFormatted
                              ? formatNumber(event.amountFormatted)
                              : "-"}
                          </td>
                          <td className="p-3">
                            {currentContractInfo?.explorerUrl ? (
                              <a
                                href={`${currentContractInfo.explorerUrl}/tx/${event.transactionHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline inline-flex items-center gap-1"
                              >
                                {formatAddress(event.transactionHash)}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <code className="text-sm">{formatAddress(event.transactionHash)}</code>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                {editingId ? "Editar Contrato" : "Novo Contrato Stablecoin"}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="id">ID *</Label>
                  <Input
                    id="id"
                    placeholder="BRLD_TESTNET"
                    value={formData.id}
                    onChange={(e) =>
                      setFormData({ ...formData, id: e.target.value.toUpperCase() })
                    }
                    disabled={!!editingId}
                    className={editingId ? "bg-muted" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    placeholder="BRLD - Testnet"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Endereço do Contrato *</Label>
                <Input
                  id="address"
                  placeholder="0x..."
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="networkId">Rede Blockchain *</Label>
                <Input
                  id="networkId"
                  placeholder="XDC_TESTNET"
                  value={formData.networkId}
                  onChange={(e) => setFormData({ ...formData, networkId: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Use o ID de uma rede cadastrada em Configurações &gt; Blockchain
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="abi">
                  ABI do Contrato (JSON) {editingId && "(deixe vazio para manter atual)"}
                </Label>
                <textarea
                  id="abi"
                  className="flex min-h-[200px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  placeholder='["function name() view returns (string)", ...]'
                  value={formData.abi}
                  onChange={(e) => setFormData({ ...formData, abi: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Cole o ABI em formato JSON. Suporta formato array ou ABI completo do Hardhat.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingId ? "Salvar" : "Criar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </div>
  );
}
